/* eslint-disable react/jsx-props-no-spreading */
import { ComponentChild, FunctionalComponent, h } from 'preact';
import MapPurposes, { PurposeProps } from '../TCFHandler/MapPurposes';
import MapSpecialFeatures, { SpecialFeatureProps } from '../TCFHandler/MapSpecialFeatures';
import MapStacks, { StackProps } from '../TCFHandler/MapStacks';
import { TCFFeature, TCFUISettings } from '@usercentrics/cmp-browser-sdk';
import { forwardRef, memo } from 'preact/compat';
import { getIndexesRatio, getItemStyle, getLengthWithExtra } from '../VirtualList/helpers';
import MapCategories from '../CategoryHandler/MapCategories';
import { VIEW } from '../../enums';
import VirtualCategoryItem from '../VirtualList/VirtualCategoryItem';
import { VirtualChildContainer } from '../VirtualList/VirtualList.style';
import VirtualLegalDescriptionItem from '../VirtualList/VirtualLegalDescriptionItem';
import VirtualListWrapper from '../VirtualList';
import { useCategoryContext } from '../../contexts/CategoryContext';
import { useEffect } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useTcfContext } from '../../contexts/TcfContext';
import { useUiContext } from '../../contexts/UiContext';
import { useVirtualList } from '../../hooks';

const EXTRA_CATEGORIES_ITEMS = 1; // title
const EXTRA_FEATURES_ITEMS = 1; // title
const EXTRA_PURPOSES_ITEMS = 1; // title

type Props = {
  categoriesTitle: string;
  featuresTitle: string;
  hideNonIabPurposes?: boolean;
  isVisible: boolean;
  purposesTitle: string;
  useInlineToggle?: boolean;
  useFromStackOnly?: boolean;
};

const PurposesList: FunctionalComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      categoriesTitle,
      featuresTitle,
      hideNonIabPurposes = false,
      isVisible,
      purposesTitle,
      useInlineToggle = false,
      useFromStackOnly = false,
    },
    virtualListRef,
  ) => {
    const { tcfData, setTcfData } = useTcfContext();
    const { clearCategoryPurposeScrollToId, scrollToIdTabViewCategoryPurpose, view } = useUiContext();
    let purposes: PurposeProps[];
    let specialFeatures: SpecialFeatureProps[];
    let purposeStacks: StackProps[] = [];
    let specialFeatureStacks: StackProps[] = [];
    let features: TCFFeature[] = [];
    let specialPurposes: TCFFeature[] = [];

    // Clears the scroll to index to avoid jumping back on it after the component rendered
    useEffect(() => {
      if (isVisible) {
        clearCategoryPurposeScrollToId();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    if (useFromStackOnly) {
      purposes = MapPurposes({ setTcfData, tcfData }).filter(
        (purposeProps: PurposeProps) => !purposeProps.purpose.isPartOfASelectedStack,
      );
      specialFeatures = MapSpecialFeatures({ setTcfData, tcfData }).filter(
        (specialFeatureProps: SpecialFeatureProps) => !specialFeatureProps.specialFeature.isPartOfASelectedStack,
      );
      purposeStacks = MapStacks({ setTcfData, tcfData }).filter(
        (stackProps: StackProps) => stackProps.stack.purposeIds.length > 0,
      );
      specialFeatureStacks = MapStacks({ setTcfData, tcfData }).filter(
        (stackProps: StackProps) =>
          stackProps.stack.purposeIds.length === 0 && stackProps.stack.specialFeatureIds.length > 0,
      );
    } else {
      purposes = MapPurposes({ setTcfData, tcfData });
      specialFeatures = MapSpecialFeatures({ setTcfData, tcfData });
      features = tcfData?.features || [];
      specialPurposes = tcfData?.specialPurposes || [];
    }
    const categories = MapCategories(useCategoryContext());
    const { settings } = useSettingsContext();
    const ui = settings.ui as TCFUISettings;

    const [purposesLength, specialPurposesLength, purposeStacksLength] = getLengthWithExtra(
      EXTRA_PURPOSES_ITEMS,
      purposes,
      specialPurposes,
      purposeStacks,
    );
    const [featuresLength, specialFeaturesLength, specialFeaturesStacksLength] = getLengthWithExtra(
      EXTRA_FEATURES_ITEMS,
      features,
      specialFeatures,
      specialFeatureStacks,
    );
    const categoriesLength = categories.length > 0 ? categories.length + EXTRA_CATEGORIES_ITEMS : 0;

    const purposesCount = purposesLength + specialPurposesLength + purposeStacksLength;
    const featuresCount = featuresLength + specialFeaturesLength + specialFeaturesStacksLength;
    const categoriesCount = categoriesLength;
    const itemCount = purposesCount + featuresCount + categoriesCount;

    const { setItemSize, getItemSize } = useVirtualList(itemCount, 50);

    const getScrollIndexForId = (): number => {
      let index;

      // If scrollToId is a number or a string representation of a number --> look in vendor list

      if (Number(scrollToIdTabViewCategoryPurpose)) {
        // 1. Go through Purpose List
        index = purposes.findIndex(
          (purposeProp) => purposeProp.purpose.id === Number(scrollToIdTabViewCategoryPurpose),
        );

        if (index >= 0) return index + EXTRA_PURPOSES_ITEMS;

        // 2. Go through Special Purpose List
        index = specialPurposes.findIndex(
          (specialPurposeProp) => specialPurposeProp.id === Number(scrollToIdTabViewCategoryPurpose),
        );

        if (index >= 0) return index + purposesLength;

        // 3. Go through Feature List
        index = features.findIndex((feature) => feature.id === Number(scrollToIdTabViewCategoryPurpose));

        if (index >= 0) return index + EXTRA_FEATURES_ITEMS + purposesCount;

        // 4. Go through Special Feature List
        index = specialFeatures.findIndex(
          (specialFeatureProp) => specialFeatureProp.specialFeature.id === Number(scrollToIdTabViewCategoryPurpose),
        );

        if (index >= 0) return index + featuresLength + purposesCount;
      } else if (scrollToIdTabViewCategoryPurpose) {
        // Id is in Category List
        index = categories.findIndex((category) => category.category.slug === scrollToIdTabViewCategoryPurpose);

        return index + purposesCount + featuresCount + EXTRA_CATEGORIES_ITEMS;
      }

      return -1;
    };

    const renderList = (index: number): ComponentChild => {
      const [
        purposesIndex,
        purposeStackIndex,
        specialPurposesIndex,
        featuresIndex,
        specialFeaturesIndex,
        specialFeatureStackIndex,
        categoriesIndex,
      ] = getIndexesRatio(
        purposesLength,
        purposeStacksLength,
        specialPurposesLength,
        featuresLength,
        specialFeaturesLength,
        specialFeaturesStacksLength,
        categoriesLength,
      );

      const hasPurposes = purposesLength > 0 || specialPurposesLength > 0 || purposeStacksLength > 0;
      const hasFeatures = featuresLength > 0 || specialFeaturesLength > 0 || specialFeaturesStacksLength > 0;

      if (hasPurposes) {
        if (purposesLength > 0 && index < purposesIndex.end) {
          return (
            <VirtualLegalDescriptionItem
              data={purposes}
              index={index}
              id={index}
              getItemSize={getItemSize}
              setItemSize={setItemSize}
              testid="uc-tcf-purposes-expandable"
              title={purposesTitle}
              useInlineToggle={useInlineToggle}
              useFromStackOnly={useFromStackOnly}
            />
          );
        }

        if (specialPurposesLength > 0 && index >= specialPurposesIndex.start && index < specialPurposesIndex.end) {
          return (
            <VirtualLegalDescriptionItem
              data={specialPurposes}
              index={index}
              id={index - specialPurposesIndex.start}
              getItemSize={getItemSize}
              setItemSize={setItemSize}
              testid="uc-tcf-special-purposes-expandable"
              useInlineToggle={useInlineToggle}
              useFromStackOnly={useFromStackOnly}
              {...(purposesLength > 0 ? {} : { title: purposesTitle })}
            />
          );
        }

        if (purposeStacksLength > 0 && index >= purposeStackIndex.start && index < purposeStackIndex.end) {
          return (
            <VirtualLegalDescriptionItem
              data={purposeStacks}
              index={index}
              id={index - purposeStackIndex.start}
              getItemSize={getItemSize}
              setItemSize={setItemSize}
              testid="uc-tcf-purposes-stack-expandable"
              useInlineToggle={useInlineToggle}
              useFromStackOnly={useFromStackOnly}
              {...(purposesLength > 0 ? {} : { title: purposesTitle })}
            />
          );
        }
      }

      if (hasFeatures) {
        if (featuresLength > 0 && index >= featuresIndex.start && index < featuresIndex.end) {
          return (
            <VirtualLegalDescriptionItem
              data={features}
              index={index}
              id={index - featuresIndex.start}
              getItemSize={getItemSize}
              setItemSize={setItemSize}
              testid="uc-tcf-features-expandable"
              useInlineToggle={useInlineToggle}
              useFromStackOnly={useFromStackOnly}
              title={featuresTitle}
            />
          );
        }

        if (specialFeaturesLength > 0 && index >= specialFeaturesIndex.start && index < specialFeaturesIndex.end) {
          return (
            <VirtualLegalDescriptionItem
              data={specialFeatures}
              index={index}
              id={index - specialFeaturesIndex.start}
              getItemSize={getItemSize}
              setItemSize={setItemSize}
              testid="uc-tcf-special-features-expandable"
              useInlineToggle={useInlineToggle}
              useFromStackOnly={useFromStackOnly}
              {...(featuresLength > 0 ? {} : { title: featuresTitle })}
            />
          );
        }

        if (
          specialFeaturesStacksLength > 0 &&
          index >= specialFeatureStackIndex.start &&
          index < specialFeatureStackIndex.end
        ) {
          return (
            <VirtualLegalDescriptionItem
              data={specialFeatureStacks}
              index={index}
              id={index - specialFeatureStackIndex.start}
              getItemSize={getItemSize}
              setItemSize={setItemSize}
              testid="uc-tcf-special-features-expandable"
              useInlineToggle={useInlineToggle}
              useFromStackOnly={useFromStackOnly}
              {...(featuresLength > 0 ? {} : { title: featuresTitle })}
            />
          );
        }
      }

      if (
        !hideNonIabPurposes &&
        categoriesLength > 0 &&
        index >= categoriesIndex.start &&
        index < categoriesIndex.end
      ) {
        return (
          <VirtualCategoryItem
            categories={categories}
            index={index}
            id={index - categoriesIndex.start}
            {...(scrollToIdTabViewCategoryPurpose && { scrollToId: scrollToIdTabViewCategoryPurpose })}
            getItemSize={getItemSize}
            setItemSize={setItemSize}
            showToggles={!ui.secondLayer.hideToggles}
            showServicesToggles={!ui.secondLayer.hideToggles}
            title={categoriesTitle}
            showOnlyConsentToggle={useFromStackOnly}
          />
        );
      }

      return null;
    };

    // Do not keep second layer scroll - If current view is First Layer we reset the list scroll position.
    const addScroll = view !== VIEW.FIRST_LAYER && scrollToIdTabViewCategoryPurpose;

    // To avoid the white space when you decide not to show anything
    if (purposesCount === 0 && featuresCount === 0 && hideNonIabPurposes) {
      return null;
    }

    return (
      <VirtualListWrapper
        data-testid="uc-virtual-list"
        ref={virtualListRef}
        estimatedItemSize={50}
        isVisible={isVisible}
        itemCount={itemCount}
        itemSize={getItemSize}
        {...(addScroll && { scrollToIndex: getScrollIndexForId() })}
        renderItem={({ index, style }): ComponentChild => (
          <VirtualChildContainer data-testid={`uc-virtual-list-item-${index}`} key={index} style={getItemStyle(style)}>
            {renderList(index)}
          </VirtualChildContainer>
        )}
        width="100%"
      />
    );
  },
);

export default memo(PurposesList);
